import { AtomContainer } from "@alchemiesolns/atomcontainer";
import { formatViewBoxToken } from "../utils/viewBoxCalculations";
import { visibilityConfig, VisibilityFlag } from "@alchemiesolns/visibility-config";
import { generateAtomRecipes } from "./atomRecipes";
import { getRecipeLabelRectChem } from "./atomLabelBounds";
import { getFormat } from "./utils/formatContext";

// Extended bounding box that includes text and other elements
export interface ExtendedBounds {
  minX: number
  maxX: number  
  minY: number
  maxY: number
  width: number
  height: number
}

/**
 * Radial slack from atom center for lone pairs / charges / similar (not full 3.0 when unused).
 */
function decorationSlackAroundAtom(
  lonePairs: number,
  radicals: number,
  visFlag: VisibilityFlag | undefined
): number {
  const lpSuppressed =
    visFlag === VisibilityFlag.HiddenLP ||
    visFlag === VisibilityFlag.ForceTextHiddenLP;
  const needsLpRoom = lonePairs > 0 && !lpSuppressed;
  if (needsLpRoom) {
    return 2.35;
  }
  if (radicals > 0) {
    return 1.85;
  }
  return 1.15;
}

/**
 * Calculates extended bounding box including atom labels, lone pairs, and charges
 * @param config - Configuration string for the molecule
 * @param includeExtraSpace - Whether to include extra space for decorations (default: true)
 * @param minPadding - Minimum padding around atoms when includeExtraSpace is false (default: 2.0)
 */
export const calculateExtendedBounds = (
  config: string,
  includeExtraSpace: boolean = true,
  minPadding: number = 2.0
): ExtendedBounds => {
  try {
    const container = AtomContainer.decode(config)

    if (container.atoms.length === 0) {
      return { minX: 0, maxX: 10, minY: 0, maxY: 10, width: 10, height: 10 }
    }

    // Validate all atoms have positions (should be rare exception - cut/paste error)
    for (let i = 0; i < container.atoms.length; i++) {
      const atom = container.atoms[i];
      if (!atom.pos) {
        // console.warn(
        //   `[svgBounds] Atom ${i} missing position data (symbol=${atom.symbol}, idx=${atom.idx}). ` +
        //   `This indicates an invalid config string. Using default bounds.`
        // );
        return { minX: 0, maxX: 10, minY: 0, maxY: 10, width: 10, height: 10 };
      }
    }

    // Parse visibility configuration from the config string
    const visDefs = visibilityConfig.decode(config);

    const visibleAtoms = container.atoms.filter((_atom, index) => {
      const visDef = visDefs.find(v => v.a === index);
      return visDef?.visibilityFlag !== VisibilityFlag.Hidden;
    });

    const atomRecipes = generateAtomRecipes(
      container.atoms,
      container,
      visDefs,
      visibleAtoms
    );
    const recipeByIndex = new Map(atomRecipes.map((r) => [r.atomIndex, r]));
    const fontSize = getFormat().fontSize;

    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity

    // Process each visible atom and its potential label space
    container.atoms.forEach((atom, index) => {
      if (!atom.pos) return
      
      // Check if this atom is hidden according to visDefs
      const visDef = visDefs.find(v => v.a === index);
      if (visDef?.visibilityFlag === VisibilityFlag.Hidden) {
        // Skip hidden atoms (implicit hydrogens)
        // console.log(`[svgBounds] Atom ${atom.idx} is hidden, skipping`)
        return
      }

      const x = atom.pos.x
      const y = atom.pos.y

      if (includeExtraSpace) {
        const recipe = recipeByIndex.get(index);
        if (recipe) {
          const labelRect = getRecipeLabelRectChem(recipe, x, y, fontSize);
          minX = Math.min(minX, labelRect.minX)
          maxX = Math.max(maxX, labelRect.maxX)
          minY = Math.min(minY, labelRect.minY)
          maxY = Math.max(maxY, labelRect.maxY)
        } else {
          const fallbackPad = fontSize * 2.4
          minX = Math.min(minX, x - fallbackPad)
          maxX = Math.max(maxX, x + fallbackPad)
          minY = Math.min(minY, y - fallbackPad)
          maxY = Math.max(maxY, y + fallbackPad)
        }

        const deco = decorationSlackAroundAtom(
          atom.lonePairs,
          atom.radicals,
          visDef?.visibilityFlag
        );
        minX = Math.min(minX, x - deco)
        maxX = Math.max(maxX, x + deco)
        minY = Math.min(minY, y - deco)
        maxY = Math.max(maxY, y + deco)
      } else {
        // Use tight bounds - just atom positions plus minimal padding
        minX = Math.min(minX, x - minPadding)
        maxX = Math.max(maxX, x + minPadding)
        minY = Math.min(minY, y - minPadding)
        maxY = Math.max(maxY, y + minPadding)
      }
    })

    // Process bonds to ensure they're included in bounds
    // Only include bonds where both atoms are visible
    container.bonds.forEach(bond => {
      if (bond.a1?.pos && bond.a2?.pos) {
        // Get atom indices for visibility check (use indexOf with object reference)
        const a1Index = container.atoms.indexOf(bond.a1);
        const a2Index = container.atoms.indexOf(bond.a2);
        
        // Check if either atom is hidden according to visDefs
        const visDef1 = visDefs.find(v => v.a === a1Index);
        const visDef2 = visDefs.find(v => v.a === a2Index);
        
        // Skip bonds connected to hidden atoms
        if (visDef1?.visibilityFlag === VisibilityFlag.Hidden || 
            visDef2?.visibilityFlag === VisibilityFlag.Hidden) {
          return;
        }

        minX = Math.min(minX, bond.a1.pos.x, bond.a2.pos.x)
        maxX = Math.max(maxX, bond.a1.pos.x, bond.a2.pos.x)
        minY = Math.min(minY, bond.a1.pos.y, bond.a2.pos.y)
        maxY = Math.max(maxY, bond.a1.pos.y, bond.a2.pos.y)
      }
    })
    
    const width = maxX - minX
    const height = maxY - minY

    return { minX, maxX, minY, maxY, width, height }
    
  } catch (error) {
    console.error('Error calculating extended bounds:', error)
    return { minX: 0, maxX: 10, minY: 0, maxY: 10, width: 10, height: 10 }
  }
}

/**
 * Expands a viewBox string by adding uniform padding on all sides.
 * Keeps the center point fixed and grows width/height by 2*padding.
 */
export function expandViewBox(viewBox: string, padding: number): string {
  const [x, y, w, h] = viewBox.split(' ').map(Number);
  return `${formatViewBoxToken(x - padding)} ${formatViewBoxToken(y - padding)} ${formatViewBoxToken(w + 2 * padding)} ${formatViewBoxToken(h + 2 * padding)}`;
}

/**
 * Calculates optimal viewBox for given SVG dimensions and content bounds
 * @param alignment - Horizontal alignment when content is taller than SVG aspect ratio
 */
export const calculateOptimalViewBox = (
  svgWidth: number,
  svgHeight: number,
  bounds: ExtendedBounds,
  alignment: 'left' | 'center' | 'right' = 'center'
): string => {
  // Safeguard: Check if bounds are valid (not NaN/Infinity)
  // This can happen if atoms don't have valid coordinates yet
  if (
    !Number.isFinite(bounds.minX) ||
    !Number.isFinite(bounds.maxX) ||
    !Number.isFinite(bounds.minY) ||
    !Number.isFinite(bounds.maxY) ||
    !Number.isFinite(bounds.width) ||
    !Number.isFinite(bounds.height) ||
    bounds.width <= 0 ||
    bounds.height <= 0
  ) {
    // console.warn(
    //   '[svgBounds] Invalid bounds detected (NaN/Infinity or non-positive dimensions), using default viewBox',
    //   bounds
    // );
    // Return safe default viewBox that matches aspect ratio
    const aspect = svgWidth / svgHeight;
    const defaultHeight = 100;
    const defaultWidth = defaultHeight * aspect;
    return `0 0 ${formatViewBoxToken(defaultWidth)} ${formatViewBoxToken(defaultHeight)}`;
  }

  const contentWidth = bounds.width
  const contentHeight = bounds.height
  const contentLeft = bounds.minX
  const contentTop = bounds.minY

  // Calculate aspect ratios
  const svgAspect = svgWidth / svgHeight
  const contentAspect = contentWidth / contentHeight

  let viewBoxWidth, viewBoxHeight, viewBoxLeft, viewBoxTop

  if (contentAspect > svgAspect) {
    // Content is wider - fit width, center height
    viewBoxWidth = contentWidth
    viewBoxHeight = contentWidth / svgAspect
    viewBoxLeft = contentLeft
    // Center content vertically by calculating center of content, then positioning viewBox
    // so the content center aligns with viewBox center
    const contentCenterY = contentTop + contentHeight / 2
    viewBoxTop = contentCenterY - viewBoxHeight / 2
  } else {
    // Content is taller (or equal) - fit height, apply horizontal alignment
    viewBoxHeight = contentHeight
    viewBoxWidth = contentHeight * svgAspect

    // Center content vertically
    const contentCenterY = contentTop + contentHeight / 2
    viewBoxTop = contentCenterY - viewBoxHeight / 2
    // console.log(`[svgBounds] alignment: ${alignment}, viewBoxTop: ${viewBoxTop}`)
    // Apply horizontal alignment
    const extraWidth = viewBoxWidth - contentWidth
    switch (alignment) {
      case 'left':
        // Align content to left edge
        viewBoxLeft = contentLeft
        break
      case 'right':
        // Align content to right edge
        viewBoxLeft = contentLeft - extraWidth
        break
      case 'center':
      default:
        // Center content horizontally by calculating center of content, then positioning viewBox
        // so the content center aligns with viewBox center
        const contentCenterX = contentLeft + contentWidth / 2
        viewBoxLeft = contentCenterX - viewBoxWidth / 2
        break
    }
  }

  return `${formatViewBoxToken(viewBoxLeft)} ${formatViewBoxToken(viewBoxTop)} ${formatViewBoxToken(viewBoxWidth)} ${formatViewBoxToken(viewBoxHeight)}`;
}

/**
 * Convenience function to calculate viewBox string directly from config
 */
export const calculateViewBoxFromConfig = (
  config: string,
  svgWidth: number,
  svgHeight: number,
  alignment: 'left' | 'center' | 'right' = 'center'
): string => {
  const bounds = calculateExtendedBounds(config)
  return calculateOptimalViewBox(svgWidth, svgHeight, bounds, alignment)
}
